/**
 * SOLID Review
 * S — Strong. One concern: the admin's LiveKit usage report — its date range (the last week by default), the report asked for, and
 *     why not.
 * O — Good. Another filter is one more field.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The range's two setters and the run.
 * D — Strong. One use case, the request contexts, the clock.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import type { IServiceViewClock } from './platform/IConfigDepViewPlatform';
import type { ClientRequests } from '../client/ClientRequests';
import type { IPortClientUseCases, TClientUseCaseInput, TClientUseCaseOutput } from '../../application/ports/ClientUseCases.contract';

type IEntityMediaUsage = TClientUseCaseOutput<'loadMediaUsageReport'>;

export interface IStateUsageReport {
  readonly startDate: string;
  readonly endDate: string;
  readonly report: IEntityMediaUsage | null;
  readonly loading: boolean;
  /** The failure's message, verbatim — the admin reads the server's diagnostic. */
  readonly error: { readonly message: string } | null;
}

const RUNNING: Partial<IStateUsageReport> = { loading: true, error: null };
const DONE: Partial<IStateUsageReport> = { loading: false };

/**
 * THE ADMIN'S USAGE REPORT (frontend-framework-oo P5, 2026-10-30, §28.297) — `components/admin/LiveKitUsageReportModal.tsx`'s state
 * and handler: the range defaults to the last seven days (UTC dates, from the clock when the report is opened).
 */
export class UsageReportViewModel implements IServiceViewModel<IStateUsageReport> {
  private readonly state: ObservableState<IStateUsageReport>;

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'loadMediaUsageReport'>,
    private readonly requests: ClientRequests,
    clock: IServiceViewClock,
  ) {
    const lastWeek: IStateUsageReport = {
      startDate: UsageReportViewModel.isoDateDaysAgo(clock.now(), 7), endDate: UsageReportViewModel.isoDateDaysAgo(clock.now(), 0),
      report: null, loading: false, error: null,
    };
    this.state = new ObservableState<IStateUsageReport>(lastWeek);
  }

  /** The UTC date `days` before `now`, as YYYY-MM-DD. */
  static isoDateDaysAgo(now: number, days: number): string {
    const d = new Date(now);
    d.setUTCDate(d.getUTCDate() - days);
    return d.toISOString().slice(0, 10);
  }

  readonly setStartDate = (startDate: string): void => {
    const picked: Partial<IStateUsageReport> = { startDate };
    this.state.update(picked);
  };

  readonly setEndDate = (endDate: string): void => {
    const picked: Partial<IStateUsageReport> = { endDate };
    this.state.update(picked);
  };

  readonly run = async (playerId: string): Promise<void> => {
    this.state.update(RUNNING);
    try {
      const { startDate, endDate } = this.state.get();
      const request: TClientUseCaseInput<'loadMediaUsageReport'> = { playerId, startDate, endDate };
      const result = await this.useCases.loadMediaUsageReport(this.requests.context(), request);
      if (!result.success) {
        const failed: Partial<IStateUsageReport> = { error: result.error };
        this.state.update(failed);
        return;
      }
      const reported: Partial<IStateUsageReport> = { report: result.value };
      this.state.update(reported);
    } finally {
      this.state.update(DONE);
    }
  };

  getSnapshot(): IStateUsageReport {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {}
}
